import { CircularProgress, InputAdornment, makeStyles, TextField } from '@material-ui/core';
import React, { SyntheticEvent, useState } from 'react';

import { ReactComponent as SearchSvg } from '../../../assets/search_2.svg';
import { usePlexesMap } from '../../../models/filters/useSelectedPlexIds';
import { spacings, topBorderStyle } from '../../../theme';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { EmptySearchStateSlate } from './EmptySearchStateSlate';
import { SearchResultItemSlate } from './SearchResultItemSlate';
import { isSearchLoading, SearchResultOption, useSearch } from './useSearch';

interface Props {
  onInsertSearchItems: (searchResult: SearchResultOption[]) => void;
  closeSearch: () => void;
}

export const SearchResultsPageSlate = React.memo(({ onInsertSearchItems, closeSearch }: Props) => {
  const classes = useStyles();
  const [selectedItems, setSelectedItems] = useState<{ [id: string]: SearchResultOption }>({});
  const [search, setSearchQuery] = useSearch(1, null, false);
  const plexesMap = usePlexesMap();

  const setSelectedItem = (searchResult: SearchResultOption, selected: boolean): void => {
    if (selected) {
      setSelectedItems({ ...selectedItems, [searchResult.id]: searchResult });
    } else {
      const { [searchResult.id]: _, ...otherItems } = selectedItems;
      setSelectedItems({ ...otherItems });
    }
  };

  const isEmpty = search.state === 'done' && search.options.length === 0;

  return (
    <div className={classes.root}>
      <TextField
        className={classes.inputContainer}
        placeholder={'Search to add in document'}
        variant={'outlined'}
        fullWidth={true}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchSvg />
            </InputAdornment>
          ),
        }}
        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
          setSearchQuery({
            input: event.currentTarget.value,
          });
        }}
      />
      <div className={classes.contentContainer}>
        <div className={classes.resultsContainer}>
          {isSearchLoading(search) && <CircularProgress size={24} />}
          {isEmpty && <EmptySearchStateSlate />}
          {search.state === 'done' &&
            search.options.map((searchResult) => (
              <SearchResultItemSlate
                key={searchResult.id}
                searchResult={searchResult}
                selected={!!selectedItems[searchResult.id]}
                onSelect={setSelectedItem}
                plexName={!!searchResult?.body?.plexId ? plexesMap[searchResult.body.plexId].name : ''}
                plexColor={!!searchResult?.body?.plexId ? plexesMap[searchResult.body.plexId].color : ''}
              />
            ))}
        </div>
        <div className={classes.footer}>
          <PlexButton
            className={classes.applyUpdatesButton}
            label={'Apply Plex Updates'}
            color={ButtonColor.Primary}
            onClick={(): void => {
              const selectedSearchResultsList = Object.values(selectedItems) as SearchResultOption[];
              onInsertSearchItems(selectedSearchResultsList);
              closeSearch();
            }}
          />
        </div>
      </div>
    </div>
  );
});

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    width: 432,
    height: 446,
  },
  inputContainer: {
    padding: spacings.normal,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    justifyContent: 'space-between',
    flex: 1,
  },
  resultsContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  applyUpdatesButton: {
    marginLeft: 'auto',
    marginBottom: spacings.normal,
    marginTop: spacings.normal,
    marginRight: spacings.normal,
  },
  footer: {
    ...topBorderStyle,
  },
}));
